import React, { useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase';
import { v4 as uuidv4 } from 'uuid';
import '../styles/UrlUpload.css';
import { Check, Trash } from 'lucide-react';

function UrlUpload({ task_id = null }) {
  const { authUserId, selectedWorkspaceId } = useWorkspace(); 
  const { language } = useLanguage();
  const [urls, setUrls] = useState([{ id: uuidv4(), value: '', note: '' }]); 
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleUrlChange = (id, value) => {
    setUrls(urls.map((url) => (url.id === id ? { ...url, value } : url)));
  };

  const handleNoteChange = (id, value) => {
    setUrls(urls.map((url) => (url.id === id ? { ...url, note: value } : url)));
  };

  const addUrlField = () => {
    setUrls([...urls, { id: uuidv4(), value: '', note: '' }]);
  };

  const removeUrlField = (id) => {
    if (urls.length > 1) {
      setUrls(urls.filter((url) => url.id !== id));
    }
  };

  const handleSubmit = async () => {
    if (urls.length === 0 || urls.every((url) => url.value.trim() === '')) {
      setError(language === 'en' ? "Please enter at least one URL before submitting." : "Bitte geben Sie mindestens eine URL ein, bevor Sie absenden.");
      return;
    }

    if (!authUserId || !selectedWorkspaceId) {
      setError(language === 'en' ? "Invalid session: missing user or workspace." : "Ungültige Sitzung: Benutzer oder Arbeitsbereich fehlt.");
      return;
    }

    setError('');
    setSuccessMessage('');

    const timestamp = new Date().toISOString();
    const orgId = "7e8835a5-5c90-487a-82b0-62758d80435c";
    const generatedDocIds = [];

    try {
      for (let { value, note } of urls) {
        if (!value.trim()) continue; 

        const documentId = uuidv4();
        generatedDocIds.push(documentId);

        const documentData = {
          id: documentId,
          name: `URLs: ${value}`,
          created_at: timestamp,
          updated_at: timestamp,
          updated_by: authUserId,
          content: value,
          workspace_id: selectedWorkspaceId,
          type: "url/reference",
          folder_path: "input/",
          org_id: orgId,
          is_sensitive: false,
          note: note
        };

        const { error: insertError } = await supabase.from('documents').insert([documentData]);

        if (insertError) {
          throw insertError;
        }
      }

      if (task_id !== null) {
        const { error: updateError } = await supabase
          .from('tasks')
          .update({ assigned_documents: generatedDocIds })
          .eq('id', task_id);
        if (updateError) {
          throw updateError;
        }
      }

      setSuccessMessage(language === 'en' ? "URLs submitted successfully!" : "URLs erfolgreich übermittelt!");
      setUrls([{ id: uuidv4(), value: '', note: '' }]);
    } catch (err) {
      setError(language === 'en' ? `Submission failed: ${err.message}` : `Übermittlung fehlgeschlagen: ${err.message}`);
    }
  };

  return (
    <div className="url-upload-container">
      {urls.map((url, index) => (
        <div key={url.id} className="url-input-row">
          <input
            type="text"
            placeholder={language === 'en' ? "Enter URL..." : "URL eingeben..."}
            value={url.value}
            onChange={(e) => handleUrlChange(url.id, e.target.value)}
            className="url-input"
          />
          {urls.length > 1 && (
            <button className="remove-url-button" onClick={() => removeUrlField(url.id)}>
              <Trash size={16} />
            </button>
          )}
          <div className="url-note">
            <input 
              type="text"
              placeholder={language === 'en' ? "You can add notes here." : "Sie können hier Notizen hinzufügen."}
              value={url.note}
              onChange={(e) => handleNoteChange(url.id, e.target.value)}
              className="note-input"
            />
          </div>
        </div>
      ))}

      <button className="add-url-button" onClick={addUrlField}>
        {language === 'en' ? '+ Add URL' : '+ URL hinzufügen'}
      </button>
      {error && <p className="url-error-text">{error}</p>}
      {successMessage && <p className="url-success-text">{successMessage}</p>}

      <button 
        className="url-submit-button" 
        onClick={handleSubmit}
        style={{ display: urls.some(url => url.value.trim() !== '') ? 'block' : 'none' }}
      >
        <Check size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
        {language === 'en' ? 'Submit' : 'Absenden'}
      </button>
    </div>
  );
}

export default UrlUpload;
